import { IconName } from 'app/shared/igntr-icons';
import { ContentLanguage } from 'app/api/models/user/content-language';

export interface SalesProfileOptionStructure {
  /** Stable id (A–E) used for local selection tracking and to join the wording on. */
  id: string;
  /**
   * Methodology tag the answer maps to (track for Q1, focus area for Q2–Q5,
   * motivation for Q6). Never translated and never editable: the tags are the
   * keys the scoring, the focus-area descriptions and the sprint matching all
   * run on. Only the wording next to them is admin-editable, and it comes from
   * the server.
   */
  tag: string;
}

export interface SalesProfileQuestionStructure {
  number: number;
  /** Minimum answers required to advance (2 for Q1–Q5, 0 for the optional Q6). */
  min: number;
  /** Maximum answers selectable. */
  max: number;
  options: SalesProfileOptionStructure[];
  hasFreeText?: boolean;
}

/** Icons for the three intro steps, in the order the steps are stored. */
export const SALES_PROFILE_STEP_ICONS: IconName[] = ['search', 'bulb', 'check'];

export const SALES_PROFILE_QUESTIONS: SalesProfileQuestionStructure[] = [
  {
    number: 1,
    min: 2,
    max: 2,
    options: [
      { id: 'A', tag: 'JAGT' },
      { id: 'B', tag: 'JAGT' },
      { id: 'C', tag: 'FASTHOLD' },
      { id: 'D', tag: 'FASTHOLD' },
      { id: 'E', tag: 'UDVID' },
    ],
  },
  {
    number: 2,
    min: 2,
    max: 2,
    options: [
      { id: 'A', tag: 'Mødeforberedelse' },
      { id: 'B', tag: 'Læse kundetyper' },
      { id: 'C', tag: 'Relationsopbygning & tillid' },
      { id: 'D', tag: 'Digital/telefonisk salgsdialog' },
      { id: 'E', tag: 'Spørgeteknik' },
    ],
  },
  {
    number: 3,
    min: 2,
    max: 2,
    options: [
      { id: 'A', tag: 'Aktiv lytning' },
      { id: 'B', tag: 'Behovsafdækning' },
      { id: 'C', tag: 'Kropssprog & tilstedeværelse' },
      { id: 'D', tag: 'Storytelling & cases' },
      { id: 'E', tag: 'Læse kundetyper' },
    ],
  },
  {
    number: 4,
    min: 2,
    max: 2,
    options: [
      { id: 'A', tag: 'Værdiskabende præsentation' },
      { id: 'B', tag: 'Indvendingshåndtering' },
      { id: 'C', tag: 'Forhandlingsteknik & prisforsvar' },
      { id: 'D', tag: 'Spørgeteknik' },
      { id: 'E', tag: 'Lukketeknik' },
    ],
  },
  {
    number: 5,
    min: 2,
    max: 2,
    options: [
      { id: 'A', tag: 'Lukketeknik' },
      { id: 'B', tag: 'Opfølgning & pipeline-disciplin' },
      { id: 'C', tag: 'Selvledelse & mindset' },
      { id: 'D', tag: 'Aktiv lytning' },
      { id: 'E', tag: 'Mødeforberedelse' },
    ],
  },
  {
    number: 6,
    min: 2,
    max: 2,
    hasFreeText: true,
    options: [
      { id: 'A', tag: 'Struktur & konsistens' },
      { id: 'B', tag: 'Resultater & performance' },
      { id: 'C', tag: 'Tryghed & selvsikkerhed' },
      { id: 'D', tag: 'Faglig mestring' },
      { id: 'E', tag: 'Konkurrence & anerkendelse' },
    ],
  },
];

/**
 * Display names for the methodology tags. The tag itself is always the Danish
 * name — it is the key the scoring, the focus-area descriptions and the sprint
 * matching run on, so it is never translated in storage. This map only makes it
 * readable when the copy being edited or shown is in another language.
 */
const ENGLISH_TAG_LABELS: Record<string, string> = {
  JAGT: 'Hunt',
  FASTHOLD: 'Retain',
  UDVID: 'Expand',
  Mødeforberedelse: 'Meeting preparation',
  'Læse kundetyper': 'Reading customer types',
  'Relationsopbygning & tillid': 'Relationship building & trust',
  'Digital/telefonisk salgsdialog': 'Digital/phone sales dialogue',
  Spørgeteknik: 'Questioning technique',
  'Aktiv lytning': 'Active listening',
  Behovsafdækning: 'Needs discovery',
  'Kropssprog & tilstedeværelse': 'Body language & presence',
  'Storytelling & cases': 'Storytelling & cases',
  'Værdiskabende præsentation': 'Value-driven presentation',
  Indvendingshåndtering: 'Objection handling',
  'Forhandlingsteknik & prisforsvar': 'Negotiation & price defence',
  Lukketeknik: 'Closing technique',
  'Opfølgning & pipeline-disciplin': 'Follow-up & pipeline discipline',
  'Selvledelse & mindset': 'Self-leadership & mindset',
  'Struktur & konsistens': 'Structure & consistency',
  'Resultater & performance': 'Results & performance',
  'Tryghed & selvsikkerhed': 'Confidence & self-assurance',
  'Faglig mestring': 'Professional mastery',
  'Konkurrence & anerkendelse': 'Competition & recognition',
};

export function salesProfileTagLabel(
  tag: string,
  language: ContentLanguage | null | undefined,
): string {
  return language === ContentLanguage.English
    ? (ENGLISH_TAG_LABELS[tag] ?? tag)
    : tag;
}
